// WHY (#1061/#1163): per-carrier insurance quote surface. Rendered
//   inside CarrierBidCard between the bids list and the carrier-action
//   footer. Only mounted when the parent request opted in
//   (`request.insuranceRequested === true`).
//
//   #1163 widened the model from "at most one quote per carrier" to
//   "one primary REQUEST + optional ITEM/BOX/BOX_ITEM/BOX_GROUP
//   overrides". This file is the thin container: it renders the list
//   of quotes via `CarrierInsuranceQuoteRow` and a "+ override"
//   affordance that opens `CarrierInsuranceScopePicker`. Both children
//   live in sibling files so this file stays under the 400-line soft
//   cap.
//
// @aiInvariant The primary REQUEST row is always rendered first (the
//   wire array is already ordered). Primary delete is disabled when
//   any override exists for the same carrier — mirrors the rates
//   per-scope contract from #703. Read-only for non-manager roles.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { CarrierBidScope } from '@magimp/shared';

// WHY (#1163): narrowed TFunction alias for the scope-label formatter
//   — the bundled i18next `TFunction` enumerates ~900 literal keys
//   which makes generic helpers break under strict typing. The
//   `unknown` shape relaxes the parameter side but keeps the
//   `string` return type; the function-side scope-label keys still
//   live in the locale files and participate in `i18n:check:strict`.
type ScopeLabelT = TFunction<readonly ['requests', 'common']>;
import { Button } from '../../../../ui/index.js';
import { formatMoney } from '../../format.js';
import { useAddCarrierInsuranceQuote } from '../../queries.js';
import { errorMessage } from '../../../../api/errors.js';
import type { CarrierBid, CarrierInsuranceQuote, RequestView } from '../../types.js';
import { CarrierInsuranceQuoteRow } from './CarrierInsuranceQuoteRow.js';
import { CarrierInsuranceScopePicker } from './CarrierInsuranceScopePicker.js';

export interface CarrierInsuranceSectionProps {
  requestId: string;
  request: RequestView;
  bid: CarrierBid;
  insuranceRequested: boolean;
  cargoValue: number | null;
  cargoCurrency: 'USD' | 'RUB' | 'CNY' | null;
  canManage: boolean;
}

export function CarrierInsuranceSection({
  requestId,
  request,
  bid,
  insuranceRequested,
  cargoValue,
  cargoCurrency,
  canManage,
}: CarrierInsuranceSectionProps) {
  const { t } = useTranslation(['requests', 'common']);
  const add = useAddCarrierInsuranceQuote(requestId);
  const [adding, setAdding] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!insuranceRequested) return null;

  const quotes = bid.insuranceQuotes ?? [];
  const hasPrimary = quotes.some((q) => q.scope === 'REQUEST');
  const overrideCount = quotes.filter((q) => q.scope !== 'REQUEST').length;

  function scopeLabel(quote: CarrierInsuranceQuote): string {
    return formatScopeLabel(quote, request, t);
  }

  function onPickerSubmit(input: {
    scope: CarrierBidScope;
    scopeRefId: string | null;
    percent: number;
    comment: string;
  }) {
    setSubmitError(null);
    add.mutate(
      {
        requestCarrierId: bid.id,
        body: {
          scope: input.scope,
          scopeRefId: input.scopeRefId,
          percent: input.percent,
          comment: input.comment,
        },
      },
      {
        onSuccess: () => setAdding(false),
        onError: (err) => setSubmitError(errorMessage(err)),
      },
    );
  }

  return (
    <div
      data-testid="carrier-insurance-section"
      style={{
        borderTop: '1px solid var(--border-2)',
        paddingTop: 10,
        marginTop: 10,
        display: 'grid',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 10,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: 'var(--fg-3)',
          }}
        >
          {t('requests:carriers.insurance.section')}
        </span>
        {cargoValue !== null && cargoCurrency !== null && (
          <span style={{ fontSize: 12, color: 'var(--fg-2)', fontVariantNumeric: 'tabular-nums' }}>
            {t('requests:carriers.insurance.cargoValueLabel')}:{' '}
            {formatMoney(cargoValue, cargoCurrency)}
          </span>
        )}
      </div>

      {quotes.length === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
            {t('requests:carriers.insurance.noQuote')}
          </span>
          {canManage && !adding && (
            <Button kind="primary" size="sm" icon="plus" onClick={() => setAdding(true)}>
              {t('requests:carriers.insurance.addQuote')}
            </Button>
          )}
        </div>
      )}

      {quotes.map((q) => (
        <CarrierInsuranceQuoteRow
          key={q.id}
          requestId={requestId}
          requestCarrierId={bid.id}
          quote={q}
          scopeLabel={scopeLabel(q)}
          canManage={canManage}
          canDelete={!(q.scope === 'REQUEST' && overrideCount > 0)}
          deleteDisabledReason={
            q.scope === 'REQUEST' && overrideCount > 0
              ? t('requests:carriers.insurance.primary.cannotDeleteWithOverrides')
              : null
          }
        />
      ))}

      {canManage && quotes.length > 0 && !adding && (
        <div>
          <Button
            kind="ghost"
            size="sm"
            icon="plus"
            onClick={() => {
              setSubmitError(null);
              setAdding(true);
            }}
          >
            {hasPrimary
              ? t('requests:carriers.insurance.addOverride')
              : t('requests:carriers.insurance.addQuote')}
          </Button>
        </div>
      )}

      {adding && canManage && (
        <CarrierInsuranceScopePicker
          request={request}
          existingQuotes={quotes}
          pending={add.isPending}
          submitError={submitError}
          onSubmit={onPickerSubmit}
          onCancel={() => {
            setAdding(false);
            setSubmitError(null);
          }}
        />
      )}

      {!adding && submitError !== null && (
        <div style={{ fontSize: 12, color: 'var(--status-error-fg)' }}>{submitError}</div>
      )}
    </div>
  );
}

// WHY (#1163): centralised label formatter — keeps the i18n key set in
//   one place and the manager read view (FR-10) can reuse it. The
//   `t` parameter uses i18next's `TFunction` so the key + opts shapes
//   continue to participate in the existing `i18n:check:strict` gate.
export function formatScopeLabel(
  quote: CarrierInsuranceQuote,
  request: RequestView,
  t: ScopeLabelT,
): string {
  if (quote.scope === 'REQUEST') return t('requests:carriers.insurance.scope.request.label');
  if (quote.scope === 'ITEM') {
    const item = request.items.find((i) => i.id === quote.scopeRefId);
    return t('requests:carriers.insurance.scope.item.label', {
      name: item?.name ?? quote.scopeRefId ?? '',
    });
  }
  const boxes = request.boxes ?? [];
  if (quote.scope === 'BOX') {
    const idx = boxes.findIndex((b) => b.id === quote.scopeRefId);
    return t('requests:carriers.insurance.scope.box.label', { n: idx >= 0 ? idx + 1 : 0 });
  }
  if (quote.scope === 'BOX_ITEM') {
    for (let i = 0; i < boxes.length; i += 1) {
      const bi = boxes[i]!.items.find((it) => it.id === quote.scopeRefId);
      if (bi) {
        return t('requests:carriers.insurance.scope.boxItem.label', { n: i + 1, name: bi.name });
      }
    }
    return t('requests:carriers.insurance.scope.boxItem.label', { n: 0, name: '' });
  }
  // BOX_GROUP — `<boxId>:<groupOrdinal>`.
  const ref = quote.scopeRefId ?? '';
  const sep = ref.indexOf(':');
  if (sep > 0) {
    const boxId = ref.slice(0, sep);
    const ord = ref.slice(sep + 1);
    const idx = boxes.findIndex((b) => b.id === boxId);
    return t('requests:carriers.insurance.scope.boxGroup.label', {
      n: idx >= 0 ? idx + 1 : 0,
      k: ord,
    });
  }
  return t('requests:carriers.insurance.scope.boxGroup.label', { n: 0, k: '' });
}
